import { useId, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { LABEL_WIDTH_CLASS, type ResolvedLabelLayout } from '@/lib/fieldLayout'
import { FieldLabel } from './FieldLabel'

export interface FieldRowControlProps {
  /** The id the label points at. Put it on the control itself. */
  id: string
  /** The error message's id while there is one. */
  describedBy?: string
}

/**
 * One field's label beside its control (z8nrz7djyx), shared by the renderer
 * and the builder canvas so both lay a field out the same way.
 *
 * A legacy row (nothing set on the field, no form default) draws exactly the
 * classes each field type drew before label layout existed: labels left and
 * centred against single-line controls, top-aligned against tall ones.
 *
 * A configured row is always top-aligned, with the label padded down to the
 * control's first line. `right` uses `sm:flex-row-reverse` so the DOM order
 * stays label first for assistive technology. `stacked` is set when a side
 * label leaves the control too narrow at any width (see resolveFieldSpan), and
 * puts the label above. Below `sm` every row stacks, as before.
 *
 * The control column is `min-w-0` so it never grows past the field: without
 * it a native date picker keeps its own width and draws over the next field
 * (z8nrz7e05f). The field's width comes from placeField instead.
 */
export function FieldRow({
  label,
  required,
  helpText,
  layout,
  stacked = false,
  tall = false,
  error,
  controlClassName,
  className,
  testId,
  children,
}: {
  label: string
  required?: boolean
  helpText?: string
  layout: ResolvedLabelLayout
  stacked?: boolean
  /** The control is taller than one line: a textarea, an image, a file list. */
  tall?: boolean
  error?: string
  /** The control column's classes. They differ per field type in legacy rows. */
  controlClassName: string
  className?: string
  testId?: string
  children: (control: FieldRowControlProps) => ReactNode
}) {
  const id = useId()
  const errorId = `${id}-error`
  const legacy = layout.legacy && !stacked
  const side = !stacked && layout.position !== 'above'

  const row = legacy ? (
    <div
      className={cn(
        'flex flex-col sm:flex-row gap-1.5 sm:gap-3',
        tall ? 'sm:items-start' : 'sm:items-center',
        className,
      )}
      data-testid={testId}
    >
      <FieldLabel
        label={label}
        required={required}
        helpText={helpText}
        htmlFor={id}
        className={tall ? 'sm:pt-2' : undefined}
      />
      <div className={cn('min-w-0', controlClassName)}>{children({ id, describedBy: error ? errorId : undefined })}</div>
    </div>
  ) : (
    <div
      className={cn(
        'flex flex-col gap-1.5',
        side && 'sm:items-start sm:gap-3',
        side && (layout.position === 'right' ? 'sm:flex-row-reverse' : 'sm:flex-row'),
        className,
      )}
      data-testid={testId}
    >
      <FieldLabel
        label={label}
        required={required}
        helpText={helpText}
        htmlFor={id}
        widthClass={side ? LABEL_WIDTH_CLASS[layout.width] : ''}
        // Lines the label up with the control's first line of text: an h-10
        // input centres its text 20px down, a textarea's first line sits higher.
        className={side ? (tall ? 'sm:pt-2' : 'sm:pt-3') : undefined}
      />
      <div className={cn('min-w-0', controlClassName)}>{children({ id, describedBy: error ? errorId : undefined })}</div>
    </div>
  )

  // Always wrapped, so an error appearing does not change the tree and remount
  // the control under the cursor.
  return (
    <div>
      {row}
      {error && (
        <p id={errorId} className='text-xs text-destructive mt-1'>
          {error}
        </p>
      )}
    </div>
  )
}
